import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import instance from '../apiInstance';
import './CompleteProfileModal.css';
import CompleteProfileFormActions from './CompleteProfileFormActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faHeart } from '@fortawesome/free-solid-svg-icons';
import {
  displayFieldLabel,
  fieldOptions,
  isEmptyAnswer,
  isSurveyComplete,
  LEILAO_QUERO_PLACEHOLDER,
  LEILAO_QUERO_VER,
  normalizeLeilaoSurveyFields,
} from './leilaoSurveyFields';

/** Tempo no leilão (lista ou sala) antes de exigir o survey (ms). */
export const LEILAO_SURVEY_DELAY_MS = 30_000;
export const LEILAO_SURVEY_DUE_KEY = 'leilao_survey_due';
export const LEILAO_SURVEY_ARMED_AT_KEY = 'leilao_survey_armed_at';
/** Só true após passar por /auctions ou /auction/:id nesta sessão (nível de consciência). */
export const LEILAO_SURVEY_ZONE_ENTERED_KEY = 'leilao_survey_zone_entered';

function isAuctionDetailPath(pathname) {
  if (!pathname.startsWith('/auction/')) return false;
  if (pathname === '/auction/simulation') return false;
  return pathname.split('/').filter(Boolean).length >= 2;
}

/** Lista ou detalhe = zona leilão (contagem começa aqui). */
function isAuctionZonePath(pathname) {
  if (pathname === '/auctions') return true;
  return isAuctionDetailPath(pathname);
}

function getDueStorage() {
  try {
    return window.localStorage;
  } catch {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  }
}

function readDueFlag() {
  const store = getDueStorage();
  if (!store) return false;
  if (store.getItem(LEILAO_SURVEY_DUE_KEY) === 'true') return true;
  try {
    if (window.sessionStorage.getItem(LEILAO_SURVEY_DUE_KEY) === 'true') {
      store.setItem(LEILAO_SURVEY_DUE_KEY, 'true');
      window.sessionStorage.removeItem(LEILAO_SURVEY_DUE_KEY);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function readArmedAt() {
  try {
    const raw = window.sessionStorage.getItem(LEILAO_SURVEY_ARMED_AT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeArmedAt(ts = Date.now()) {
  try {
    window.sessionStorage.setItem(LEILAO_SURVEY_ARMED_AT_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

function clearArmedAt() {
  try {
    window.sessionStorage.removeItem(LEILAO_SURVEY_ARMED_AT_KEY);
  } catch {
    /* ignore */
  }
}

function markAuctionZoneEntered() {
  try {
    window.sessionStorage.setItem(LEILAO_SURVEY_ZONE_ENTERED_KEY, 'true');
  } catch {
    /* ignore */
  }
}

function hasEnteredAuctionZone() {
  try {
    return window.sessionStorage.getItem(LEILAO_SURVEY_ZONE_ENTERED_KEY) === 'true';
  } catch {
    return false;
  }
}

function clearAuctionZoneEntered() {
  try {
    window.sessionStorage.removeItem(LEILAO_SURVEY_ZONE_ENTERED_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Survey leilão:
 * - Entrou em /auctions ou /auction/:id → marca início da contagem (~30s).
 * - Saiu pro resto do app → contagem continua; popup abre onde ela estiver.
 * - Não enviou → due persiste em qualquer página até submit.
 * - Enviou → backend não devolve pending (recorrência 7 dias).
 */
const LeilaoSurveyModal = () => {
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const surveyTimerRef = useRef(null);
  const fetchingRef = useRef(false);

  const markDue = useCallback(() => {
    const store = getDueStorage();
    if (store) store.setItem(LEILAO_SURVEY_DUE_KEY, 'true');
    try {
      window.sessionStorage.removeItem(LEILAO_SURVEY_DUE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const clearDue = useCallback(() => {
    const store = getDueStorage();
    if (store) store.removeItem(LEILAO_SURVEY_DUE_KEY);
    try {
      window.sessionStorage.removeItem(LEILAO_SURVEY_DUE_KEY);
    } catch {
      /* ignore */
    }
    clearArmedAt();
    clearAuctionZoneEntered();
  }, []);

  const isDue = useCallback(() => readDueFlag(), []);

  const openSurvey = useCallback((data) => {
    const fields = normalizeLeilaoSurveyFields(data.fields);
    setSurvey({ ...data, fields });
    const initialAnswers = {};
    fields.forEach((f) => { initialAnswers[f.name] = ''; });
    setAnswers(initialAnswers);
    window.dispatchEvent(new Event('leilao_survey_open'));
    import('../trackerBus').then(({ trackPiece }) => {
      trackPiece('leilao_survey_shown', { button: 'modal_open', surface: 'Leilão' });
    }).catch(() => {});
  }, []);

  const fetchPending = useCallback(() => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;

    instance.get('/surveys/pending', { params: { context: 'leilao' } })
      .then((res) => {
        if (!res.data) {
          clearDue();
          return;
        }
        openSurvey(res.data);
      })
      .catch((err) => console.error('Error fetching leilao survey:', err))
      .finally(() => {
        fetchingRef.current = false;
      });
  }, [token, clearDue, openSurvey]);

  const armSurvey = useCallback(() => {
    markDue();
    fetchPending();
  }, [markDue, fetchPending]);

  const clearSurveyTimer = useCallback(() => {
    if (surveyTimerRef.current) {
      clearTimeout(surveyTimerRef.current);
      surveyTimerRef.current = null;
    }
  }, []);

  const scheduleSurveyTimer = useCallback((remainingMs) => {
    clearSurveyTimer();
    if (remainingMs <= 0) {
      armSurvey();
      return;
    }
    surveyTimerRef.current = setTimeout(armSurvey, remainingMs);
  }, [armSurvey, clearSurveyTimer]);

  // Contagem global: só após entrar na zona leilão → 30s → popup em qualquer página
  useEffect(() => {
    if (!token || survey) return undefined;

    const inZone = isAuctionZonePath(location.pathname);
    if (inZone) markAuctionZoneEntered();

    // Nunca dispara para quem não passou pelo leilão nesta sessão (home/promos/etc.)
    if (!hasEnteredAuctionZone()) return undefined;

    if (isDue()) {
      fetchPending();
      return undefined;
    }

    let armedAt = readArmedAt();
    if (!armedAt && inZone) {
      armedAt = Date.now();
      writeArmedAt(armedAt);
      import('../trackerBus').then(({ trackPiece }) => {
        trackPiece('leilao_survey_timer_started', {
          button: 'auction_zone_enter',
          surface: 'Leilão',
          page: location.pathname,
        });
      }).catch(() => {});
    }

    if (!armedAt) return undefined;

    const remaining = LEILAO_SURVEY_DELAY_MS - (Date.now() - armedAt);
    scheduleSurveyTimer(remaining);

    return clearSurveyTimer;
  }, [
    token,
    survey,
    location.pathname,
    isDue,
    fetchPending,
    scheduleSurveyTimer,
    clearSurveyTimer,
  ]);

  useEffect(() => {
    const onTrigger = () => {
      if (!hasEnteredAuctionZone()) return;
      markDue();
      fetchPending();
    };
    window.addEventListener('trigger_leilao_survey', onTrigger);
    return () => window.removeEventListener('trigger_leilao_survey', onTrigger);
  }, [markDue, fetchPending]);

  useEffect(() => {
    if (!survey) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const blockEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', blockEscape, true);
    };
  }, [survey]);

  const closeSurvey = () => {
    setSurvey(null);
    clearDue();
    window.dispatchEvent(new Event('leilao_survey_closed'));
  };

  const handleChange = (name, value) => {
    setAnswers({ ...answers, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const field of survey.fields) {
      if (field.required && isEmptyAnswer(field, answers[field.name])) {
        setError(`Por favor, preencha: ${displayFieldLabel(field)}`);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await instance.post(`/surveys/${survey.id}/submit`, { answers });
      if (res.data?.points_awarded > 0) {
        alert(`Parabéns! Você ganhou ${res.data.points_awarded} pontos!`);
      }
      import('../trackerBus').then(({ trackPiece }) => {
        trackPiece('leilao_survey_submitted', { button: 'submit', surface: 'Leilão' });
      }).catch(() => {});
      closeSurvey();
    } catch (err) {
      console.error('Error submitting leilao survey:', err);
      setError('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!survey) return null;

  const canSubmit = isSurveyComplete(survey.fields, answers) && !loading;

  return (
    <div className="complete-profile-overlay">
      <div className="complete-profile-modal">
        <div className="complete-profile-header">
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16, width: 56, height: 48 }}>
            <FontAwesomeIcon icon={faGift} size="3x" color="#D4AF37" />
            <FontAwesomeIcon
              icon={faHeart}
              style={{ position: 'absolute', right: -10, bottom: 4, color: '#E85D75', fontSize: 22 }}
            />
          </div>
          <h2>{survey.title}</h2>
          {survey.description && <p>{survey.description}</p>}

          {survey.reward_points > 0 && (
            <div className="complete-profile-points-badge">
              <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
              Ganhe {survey.reward_points} Pontos
            </div>
          )}
        </div>

        <form className="complete-profile-form" onSubmit={handleSubmit}>
          <div className="complete-profile-form-body">
          {survey.fields.map((field, idx) => (
            <div key={idx} className="complete-profile-input-group">
              <label>{displayFieldLabel(field)} {field.required && '*'}</label>

              {field.type === 'text' && (
                <input
                  type="text"
                  value={answers[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  placeholder={field.name === LEILAO_QUERO_VER ? LEILAO_QUERO_PLACEHOLDER : undefined}
                />
              )}

              {field.type === 'radio' && (
                <div className="complete-profile-radio-group">
                  {fieldOptions(field).map((opt, i) => (
                    <div className="complete-profile-radio-option" key={i}>
                      <input
                        type="radio"
                        name={field.name}
                        id={`leilao-${field.name}-${i}`}
                        value={opt}
                        checked={answers[field.name] === opt}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        required={field.required && !answers[field.name]}
                      />
                      <label className="complete-profile-radio-label" htmlFor={`leilao-${field.name}-${i}`}>
                        {opt}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
          <CompleteProfileFormActions
            error={error}
            hint="Flor, preencha para continuar"
            showHint={!canSubmit}
            loading={loading}
            canSubmit={canSubmit}
            loadingLabel="Continuando..."
            submitLabel={survey.reward_points > 0 ? 'Continuar e Ganhar Pontos' : 'Continuar'}
          />
        </form>
      </div>
    </div>
  );
};

export default LeilaoSurveyModal;
